import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Modal } from "./common";
import { MODES, ORIGINS, RESULTS, RESULT_COLORS, monthTitle } from "../lib/appt";

function OriginTable({ rows }) {
  if (!rows.length) return <p className="appt-rep-hint">Bu ay eklenen aday yok</p>;
  return (
    <ul className="appt-rep-bars origin" data-testid="appt-report-origins">
      {rows.map(([o, n, done]) => (
        <li key={o} data-testid={`appt-report-origin-${o}`}>
          <span>{o}</span>
          <div className="appt-rep-track"><div className="fill" style={{ width: `${(done / n) * 100}%` }} /></div>
          <b>{done}/{n}</b>
        </li>
      ))}
    </ul>
  );
}

const inMonth = (s, key) => (s || "").slice(0, 7) === key;
const localMonth = (iso) => (iso ? new Date(iso).toLocaleDateString("sv-SE").slice(0, 7) : "");

function Bars({ rows, total, testPrefix }) {
  return (
    <ul className="appt-rep-bars">
      {rows.map(([label, n, color]) => (
        <li key={label} data-testid={`${testPrefix}-${label}`}>
          <span>{label}</span>
          <div className="appt-rep-track"><div className={`fill ${color || "blue"}`} style={{ width: `${total ? (n / total) * 100 : 0}%` }} /></div>
          <b>{n}</b>
        </li>
      ))}
    </ul>
  );
}

export function AppointmentReport({ appointments, leads, customers, onClose }) {
  const now = new Date();
  const [[y, m], setYm] = useState([now.getFullYear(), now.getMonth()]);
  const key = `${y}-${String(m + 1).padStart(2, "0")}`;
  const shift = (d) => { const n = new Date(y, m + d, 1); setYm([n.getFullYear(), n.getMonth()]); };
  const r = useMemo(() => {
    const list = appointments.filter((a) => inMonth(a.date, key));
    const converted = customers.filter((c) => localMonth(c.leadConvertedAt) === key);
    const cohort = [...leads.map((l) => ({ at: l.createdAt, done: false, o: l.origin })), ...customers.filter((c) => c.leadCreatedAt).map((c) => ({ at: c.leadCreatedAt, done: true, o: c.leadOrigin }))]
      .filter((x) => localMonth(x.at) === key);
    return {
      total: list.length,
      byResult: [...RESULTS.map((s) => [s, list.filter((a) => a.result === s).length, RESULT_COLORS[s]]), ["Sonuç bekleyen", list.filter((a) => !a.result).length, "slate"]],
      byMode: MODES.map((s) => [s, list.filter((a) => a.mode === s).length]),
      newLeads: cohort.length,
      cohortDone: cohort.filter((x) => x.done).length,
      converted: converted.length,
      convertedNames: converted.map((c) => c.name),
      origins: [...ORIGINS, "Belirtilmemiş"].map((o) => {
        const g = cohort.filter((x) => (x.o || "Belirtilmemiş") === o);
        return [o, g.length, g.filter((x) => x.done).length];
      }).filter((r2) => r2[1] > 0),
    };
  }, [appointments, leads, customers, key]);
  const rate = r.newLeads ? Math.round((r.cohortDone / r.newLeads) * 100) : 0;
  return (
    <Modal title="Aylık randevu raporu" onClose={onClose}>
      <div className="appt-report" data-testid="appt-report">
        <div className="mini-cal-head">
          <button type="button" className="icon-button" onClick={() => shift(-1)} data-testid="appt-report-prev"><ChevronLeft size={16} /></button>
          <strong data-testid="appt-report-month">{monthTitle(y, m)}</strong>
          <button type="button" className="icon-button" onClick={() => shift(1)} data-testid="appt-report-next"><ChevronRight size={16} /></button>
        </div>
        <div className="appt-rep-kpis">
          <div><span>Randevu</span><strong data-testid="appt-report-total">{r.total}</strong></div>
          <div><span>Yeni aday</span><strong data-testid="appt-report-new-leads">{r.newLeads}</strong></div>
          <div><span>Müşteriye dönüşen</span><strong data-testid="appt-report-converted">{r.converted}</strong></div>
          <div><span>Aday dönüşüm oranı</span><strong data-testid="appt-report-rate">{`%${rate}`}</strong></div>
        </div>
        <small className="appt-rep-hint">Dönüşüm oranı: bu ay eklenen {r.newLeads} adaydan {r.cohortDone} tanesi müşteri oldu.</small>
        <h4>Aday kaynakları <small className="appt-rep-hint">(dönüşen / eklenen)</small></h4>
        <OriginTable rows={r.origins} />
        <h4>Görüşme sonuçları</h4>
        <Bars rows={r.byResult} total={r.total} testPrefix="appt-report-result" />
        <h4>Görüşme şekli</h4>
        <Bars rows={r.byMode} total={r.total} testPrefix="appt-report-mode" />
        {r.convertedNames.length > 0 && (<><h4>Bu ay müşteriye dönüşenler</h4><p className="appt-rep-names" data-testid="appt-report-converted-names">{r.convertedNames.join(", ")}</p></>)}
      </div>
    </Modal>
  );
}
